import { Directive, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Login } from '../entidade/login/login';
import { FuncaoService } from '../util/funcao.service';
import * as toastr from 'toastr';
import { BaseResourceModel } from './base-resource.model';
import { BaseResourceService } from './services/base-resource.service';
import { GlobalService } from '../util/global.service';
import { AutoComplete } from 'primeng/autocomplete';
import { Calendar } from 'primeng/calendar';

@Directive()
export abstract class BaseResourceItemsComponent<T extends BaseResourceModel> implements OnInit, OnDestroy {

  // ========================================================================
  // ----------------------- DECLARAÇÃO DE VARIAVEIS ------------------------
  // ========================================================================

  @Input() public lista: T[] = [];
  @Output() public listaChange: EventEmitter<T[]> = new EventEmitter();

  @Input() public login: Login;

  @Input() public usarAtalho: boolean = false;

  @Input() public autoAdd: boolean = false;

  protected anterior: T;

  protected tipo: 'adicionar' | 'editar';

  protected changeAfterEdit: boolean = false;

  protected unsubscribe: Subject<void> = new Subject();

  // ========================================================================
  // ------------------------------ CONSTRUTOR ------------------------------
  // ========================================================================
  constructor(
    public entidade: T,
    protected baseResourceService: BaseResourceService<T>) { }

  // ========================================================================
  // -------------------------- MÉTODOS ABSTRAÍDOS --------------------------
  // ========================================================================
  protected abstract afterInit(): void;
  protected abstract validSave(item: T): boolean;
  protected abstract beforeSave(item: T): void;
  protected afterEdit(item: T) { }

  protected validRemover(item: T): boolean {
    return true;
  }

  protected campoFoco(): ElementRef | AutoComplete | Calendar {
    return null;
  }

  @HostListener('window:keydown.control.i', ['$event'])
  public adicionarAtalho(event: KeyboardEvent) {
    event.preventDefault();
    if (this.usarAtalho) {
      if (this.disabled()) {
        let item = this.selecionarEditavel();
        if (item)
          this.salvar(item);
      }
      this.adicionar();
    }
  }

  @HostListener('window:keydown.control.o', ['$event'])
  public cancelarAtalho(event: KeyboardEvent) {
    event.preventDefault();
    if (this.usarAtalho) {
      if (this.disabled()) {
        let item = this.selecionarEditavel();
        if (item)
          this.cancelar(item, this.indexItem(item));
      }
    }
  }

  public adicionar() {
    if (this.disabled()) return;
    if (!this.lista) this.lista = [];
    let item = this.assing(this.entidade);
    this.afterEdit(item);
    item['editavel'] = true;
    this.lista.unshift(item);
    this.campoFocoEvent();
    this.calendarMascara();
    if (this.changeAfterEdit)
      this.change();
    this.tipo = 'adicionar';
  };

  public editar(item: T) {
    if (this.disabled())
      return;
    this.anterior = this.assing(item);
    this.afterEdit(item);
    item['editavel'] = true;
    this.campoFocoEvent();
    this.calendarMascara();
    if (this.changeAfterEdit)
      this.change();
    this.tipo = 'editar';
  }

  public cancelar(item: T, index: number) {
    if (!this.anterior) {
      this.lista.splice(index, 1);
    } else {
      item = this.assing(this.anterior);
      this.lista.splice(index, 1, item);
    }
    this.anterior = null;
    this.change();
  }


  public remover(item: T, index: number) {
    if (!this.validRemover(item))
      return;
    if (!item.id) {
      this.lista.splice(index, 1);
      toastr.success('Item removido com sucesso!');
      this.change();
    } else {
      this.baseResourceService.remover(item.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((data) => {
          this.lista.splice(index, 1);
          toastr.success('Item removido com sucesso!');
          this.change();
        }, (error) => toastr.error(error.error.payload));
    }
  }

  public salvar(item: T) {
    if (!this.validSave(item))
      return;
    this.beforeSave(item);
    item['editavel'] = false;
    this.anterior = null;
    this.change();
    if (this.autoAdd && this.tipo === 'adicionar')
      this.adicionar();
  }

  // ========================================================================
  // -------------------------- MÉTODOS DA CLASSE ---------------------------
  // ========================================================================


  ngOnInit(): void {
    this.lista = [];
    this.afterInit();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public compareFn(c1: any, c2: any): boolean {
    return c1 && c2 && c1.id && c2.id ? c1.id === c2.id : c1 === c2;
  }

  public disabled() {
    return !this.lista || this.lista.filter((e) => e['editavel']).length > 0;
  }

  public selecionarEditavel() {
    let item = this.lista.find((e) => e['editavel'])
    if (item)
      return item;
    return null;
  }

  public indexItem(item: T): number {
    return this.lista.indexOf(item);
  }

  private assing(model: T): T {
    return Object.assign({}, model)
  }

  protected change() {
    this.listaChange.emit(this.lista);
  }

  private calendarMascara() {
    setTimeout(() => {
      new GlobalService().calendarMascara();
    }, 500);
  }

  private campoFocoEvent() {
    setTimeout(() => {
      if (this.campoFoco())
        new FuncaoService().focarCampo(this.campoFoco());
    }, 100);
  }
}
